import React, { useState, useEffect } from 'react'
import { IoChevronForward } from 'react-icons/io5'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import { DiscoveryAccordionProps } from './index.type'
import Approvers from './Approvers'
import { FaShare } from 'react-icons/fa'
import ShareReport from '../ShareReport'

const DiscoveryAccordion = ({ list, expandedCategoryId }: DiscoveryAccordionProps) => {
    const { t, i18n } = useTranslation()
    const [expandedCategory, setExpandedCategory] = useState<string | null>(expandedCategoryId || null)

    // Sync with external expandedCategoryId prop
    useEffect(() => {
        setExpandedCategory(expandedCategoryId || null)
    }, [expandedCategoryId])

    // Use the data from props
    const dataToUse = list

    const toggleCategory = (categoryId: string) => {
        // If category is already expanded, close it
        if (expandedCategory === categoryId) {
            setExpandedCategory(null)
        } else {
            // If not expanded, close any other and open this one
            setExpandedCategory(categoryId)
        }
    }

    const handleReportAction = (reportId: string) => {
        console.log('Report action clicked for:', reportId)
        // Here you can implement the report action logic
    }

    if (dataToUse.length === 0) {
        return (
            <div className="flex justify-center items-center py-8">
                <p className="text-gray-500 text-sm">
                    {t('discoveryReports.accordion.noReportsFound')}
                </p>
            </div>
        )
    }

    return (
        <div className="w-full border border-gray-300 mx-4 mb-8 rounded-t-lg bg-[#dddddd] shadow-md">
            <div className="bg-[#dddddd] px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-300 rounded-t-lg">
                {t('discoveryReports.accordion.categoryName')}
            </div>
            {/* Categories as Accordion Rows */}
            {dataToUse.map((category, index) => (
                <div key={category.id}>
                    {/* Category Row (Accordion Header) */}
                    <div
                        className={`px-4 py-3 cursor-pointer border-b border-gray-200 flex items-center justify-between min-h-[48px] ${expandedCategory === category.id
                            ? 'bg-[#e7e5e5]'
                            : 'bg-white hover:bg-gray-50'
                            }`}
                        onClick={() => toggleCategory(category.id)}
                    >
                        <div className="flex-1 pr-4 min-w-0">
                            <span
                                className="text-sm  text-gray-700  break-words line-clamp-2 block"
                                title={i18n.language === 'fr' ? category.categoryFr : category.categoryEn}
                            >
                                {i18n.language === 'fr' ? category.categoryFr : category.categoryEn}
                            </span>
                        </div>
                        <div className="flex-shrink-0">
                            <IoChevronForward
                                size={16}
                                className={`text-gray-600 transition-transform duration-200 ease-out ${expandedCategory === category.id ? 'rotate-90' : ''
                                    }`}
                            />
                        </div>
                    </div>

                    {/* Reports Table (Accordion Content) */}
                    <div
                        className={`discovery-accordion-content bg-white px-8 overflow-hidden transition-all duration-200 ease-out ${index === dataToUse.length - 1 ? '' : 'border-b border-gray-200'
                            } ${expandedCategory === category.id
                                ? 'max-h-[2000px] opacity-100'
                                : 'max-h-0 opacity-0'
                            }`}
                    >
                        {
                            category.reports.length === 0
                                ? <div className="flex justify-center items-center h-12 text-sm text-gray-500">{t('discoveryReports.accordion.noReportsAvailable')}</div>
                                : <div>
                                    {/* Table Header - Only visible when the accordion is expanded */}
                                    <div className="bg-red px-1 py-3 discovery-accordion-grid text-sm  border-b border-gray-300 text-gray-500 font-semibold">
                                        <div className="discovery-accordion-cell truncate">
                                            {t('discoveryReports.accordion.reportName')}
                                        </div>
                                        <div className="discovery-accordion-cell truncate ">
                                            {t('discoveryReports.accordion.shortDescriptionAndTargetAudience')}
                                        </div>
                                        <div className="discovery-accordion-cell truncate text-center">
                                            {t('discoveryReports.accordion.approvers')}
                                        </div>
                                        <div className="discovery-accordion-cell text-center">
                                            {t('discoveryReports.accordion.action')}
                                        </div>
                                    </div>

                                    {/* Report Rows */}
                                    {category.reports.map((report, reportIndex) => (
                                        <div
                                            key={report.id}                                            
                                            className={`px-1 discovery-accordion-grid items-start min-h-[48px] ${reportIndex < category.reports.length - 1 ? 'border-b border-gray-300' : ''
                                                }`}
                                        >
                                            {/* Report Name */}
                                            <div className="discovery-accordion-cell">
                                                <Tooltip placement="top" title={i18n.language === 'fr' ? report.nameFr : report.nameEn} color='#4d4c4c'>
                                                    <span className="text-sm text-gray-600 break-words">
                                                        {i18n.language === 'fr' ? report.nameFr : report.nameEn}
                                                    </span>
                                                </Tooltip>
                                            </div>

                                            {/* Description */}
                                            <div className="discovery-accordion-cell">
                                                <Tooltip placement="top" title={report.descriptionEn || ''} color='#4d4c4c'>
                                                    <span className="text-sm text-gray-600 break-words">
                                                        {report.descriptionEn}
                                                    </span>
                                                </Tooltip>
                                            </div>

                                            {/* Approvers */}
                                            <div className="discovery-accordion-cell text-center">
                                                <span className="text-sm text-gray-600 break-words line-clamp-2 block">
                                                    <Approvers list={report?.approvers || ['']} />
                                                </span>
                                            </div>

                                            {/* Action */}
                                            <div className="discovery-accordion-cell flex justify-center items-center">
                                                <Tooltip placement="top" title={t('discoveryReports.accordion.requestAccessToReport')} color='#4d4c4c'>
                                                    <ShareReport reportId={report.id} powerBIReportId={report.powerBIReportId} />
                                                </Tooltip>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                        }
                    </div>
                </div>
            ))}

    
        </div>
    )
}

export default DiscoveryAccordion